import React, { useImperativeHandle, forwardRef } from "react"

import ResizableElementWrapper from "../shared/ResizableElementWrapper.component"
import TimeSeriesVariableSelector from "./TimeSeriesVariableSelector.component"
import TimeSeriesDisplayArea from "./TimeSeriesDisplayArea.component"
import { useTimeSeriesContextMenu } from "./TimeSeriesContextMenu.component"
import { useTimeSeriesChartElement } from "features/Reporting/hooks/Elements/useTimeSeriesChartElement"

import {
  DEFAULT_TIMESERIES_WIDTH,
  DEFAULT_TIMESERIES_HEIGHT,
} from "constants/dashboardElementSizes"

import {
  SlideElement,
  DataElementConfig,
} from "types/ReportingDashboard/elements.types"
import { CodebookData } from "types/codebook.types"
import { QueryInstance } from "types/engine.types"
import { ElementRef } from "../../ReportingDisplay.component"

/**
 * Element update payload for time series chart elements
 */
interface TimeSeriesElementUpdate {
  width?: number
  height?: number
}

/**
 * Props for TimeSeriesChartElement component
 */
export interface TimeSeriesChartElementProps {
  /** Element object to render */
  element: SlideElement
  /** Data structure for variable selection */
  codebook: CodebookData
  /** Instance ID for engine queries */
  queryInstance: QueryInstance | null
  /** Callback when element properties change */
  onUpdate: (elementId: string, updates: TimeSeriesElementUpdate) => void
  /** Configuration for time series chart element */
  config?: DataElementConfig | null
  /** Callback for config updates */
  onConfigUpdate: (config: DataElementConfig | null) => void
  /** Whether in dashboard mode */
  isDashboard?: boolean
  /** Callback when element deletion is requested */
  onDeleteRequest?: () => void
  /** Sequential execution request handler */
  onElementExecutionRequest?: (
    elementId: string,
    executeFunction: () => Promise<void>,
    reason?: string
  ) => void
  /** Element registration handler */
  onRegisterElementRef?: (elementId: string, ref: ElementRef | null) => void
}

/**
 * TimeSeriesChartElement Component (Phase 2 Refactored with Business Logic Hook)
 *
 * A refactored time series chart component using:
 * - useTimeSeriesChartElement: Custom hook containing all business logic
 * - TimeSeriesVariableSelector: Handles variable selection UI
 * - TimeSeriesDisplayArea: Handles chart rendering and display states
 * - TimeSeriesContextMenu: Handles context menu and submenus
 *
 * This demonstrates Phase 2 decomposition approach with extracted business logic.
 */
const TimeSeriesChartElement = forwardRef<
  ElementRef,
  TimeSeriesChartElementProps
>((props, ref) => {
  // Use the custom hook to get all business logic and state
  const timeSeriesLogic = useTimeSeriesChartElement(props)

  // Destructure what we need from the hook
  const {
    // State
    timeSeriesChartData,
    showSelects,
    isLoading,
    isQueuedForExecution,
    error,
    hasAttemptedLoad,
    hasProcessedData,

    // Config values
    selectedPrimaryVariable,
    selectedBreakdownVariable,
    selectedWaveVariable,
    selectedStatisticType,
    selectedDashboardSeries,
    selectedDashboardWaves,
    tableOperationType,

    // Hooks
    configHook,
    operatorManagement,

    // Handlers
    handleRefresh,
    handleEdit,
    handleDelete,
    handleVariableSelect,
    handleGenerate,
    handleSeriesToggle,
    handleWaveToggle,
    requestSequentialExecution,

    // Imperative handle values
    hasDataAndShouldRefresh,

    // Computed values
    selectOptions,
    formatYTickForLineChart,
    waveVarName,
    lineChartHeight,
  } = timeSeriesLogic

  // Expose methods via ref (same as before)
  useImperativeHandle(
    ref,
    () => ({
      hasDataAndShouldRefresh,
      requestSequentialExecution,
    }),
    [hasDataAndShouldRefresh, requestSequentialExecution]
  )

  // Context menu
  const { handleContextMenu, ContextMenu } = useTimeSeriesContextMenu({
    hasData: timeSeriesChartData.lineChartNivoData.length > 0,
    isDashboard: props.isDashboard || true,
    isLoading,
    seriesKeys: timeSeriesChartData.seriesKeys,
    waveIdentifiers: timeSeriesChartData.waveIdentifiers,
    selectedDashboardSeries,
    selectedDashboardWaves,
    onEdit: handleEdit,
    onRefresh: handleRefresh,
    onDelete: handleDelete,
    onSeriesToggle: handleSeriesToggle,
    onWaveToggle: handleWaveToggle,
  })

  // Styles (computed values moved to hook, style logic remains in component)
  const topBottomPaddingRem = 0.5
  const wrapperAdditionalClasses = showSelects
    ? "inline-block shadow-md"
    : "flex flex-col"

  const wrapperStyleOverrides: React.CSSProperties = {
    minWidth: "280px",
    minHeight: showSelects ? "auto" : "250px",
    overflow: "hidden",
    borderRadius: "4px",
    boxShadow:
      timeSeriesChartData.lineChartNivoData.length > 0 && !showSelects
        ? "0 4px 8px rgba(0, 0, 0, 0.1)"
        : "none",
    ...(props.element.y >= 810
      ? {
          backgroundColor: "white",
          border: "1px solid #e2e8f0",
          margin: "0 auto",
        }
      : {}),
    ...(showSelects ? { width: "350px", height: "auto" } : {}),
    display: showSelects ? "inline-block" : "flex",
    flexDirection: showSelects ? undefined : "column",
  }

  const contentContainerStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    padding: showSelects ? "0" : `${topBottomPaddingRem}rem`,
    display: "flex",
    flexDirection: "column",
    position: "relative",
    flexGrow: showSelects ? 0 : 1,
  }

  const timeSeriesHandlesConfig = {
    horizontal: true,
    vertical: true,
    corner: true,
  }

  return (
    <ResizableElementWrapper
      element={props.element}
      onUpdate={props.onUpdate}
      readonly={showSelects}
      minDimensions={{ width: 400, height: 250 }}
      defaultWidth={DEFAULT_TIMESERIES_WIDTH}
      defaultHeight={DEFAULT_TIMESERIES_HEIGHT}
      setSkipUpdateFlag={() => configHook.setSkipNextConfigUpdate(true)}
      additionalClasses={wrapperAdditionalClasses}
      styleOverrides={wrapperStyleOverrides}
      handleStyleType="visible"
      handlesConfiguration={timeSeriesHandlesConfig}
    >
      <div
        data-element-id={props.element.id}
        style={contentContainerStyle}
        onContextMenu={handleContextMenu}
      >
        {showSelects && (
          <TimeSeriesVariableSelector
            selectOptions={selectOptions}
            selectedPrimaryVariable={selectedPrimaryVariable}
            selectedBreakdownVariable={selectedBreakdownVariable}
            selectedWaveVariable={selectedWaveVariable}
            selectedStatisticType={selectedStatisticType}
            tableOperationType={tableOperationType}
            availableStatisticTypes={
              operatorManagement.availableStatisticValues
            }
            isLoading={isLoading}
            onVariableSelect={handleVariableSelect}
            onStatisticTypeChange={operatorManagement.handleStatisticTypeChange}
            onTableOperationTypeChange={
              operatorManagement.handleOperationTypeChange
            }
            onGenerate={handleGenerate}
          />
        )}

        {!showSelects && (
          <TimeSeriesDisplayArea
            timeSeriesChartData={timeSeriesChartData}
            isDashboard={props.isDashboard || true}
            selectedDashboardSeries={selectedDashboardSeries}
            selectedDashboardWaves={selectedDashboardWaves}
            waveVarName={waveVarName}
            selectedStatisticType={selectedStatisticType}
            lineChartHeight={lineChartHeight}
            hasProcessedData={hasProcessedData}
            isLoading={isLoading}
            isQueuedForExecution={isQueuedForExecution}
            isInitializing={configHook.isInitializing}
            hasAttemptedLoad={hasAttemptedLoad}
            error={error}
            formatYTickForLineChart={formatYTickForLineChart}
            onRefresh={handleRefresh}
            onEdit={handleEdit}
          />
        )}
      </div>
      {ContextMenu}
    </ResizableElementWrapper>
  )
})

TimeSeriesChartElement.displayName = "TimeSeriesChartElement"

export default TimeSeriesChartElement
